import { combineReducers } from "redux";
import _ from "lodash";

import * as actions from "../actions";

export const getSellableQuantity = (inventories) => {
  return _.sumBy(inventories, "quantity") || 0;
};

export const getDisuseQuantity = (inventories) => {
  return _.sumBy(inventories, "unavailableQuantity") || 0;
};

export const getHistoryFileNameByType = (type) => {
  switch (type) {
    case "group":
      return "inventory_group_history";
    case "asset":
      return "자산 리포트";
    case "single":
    default:
      return "inventory_history";
  }
};

export const getTransferStatusLabel = (transferStatus) => {
  switch (transferStatus) {
    case "request":
      return "요청";
    case "pending":
      return "대기";
    case "complete":
      return "완료";
    case "reject":
      return "거절";
    default:
      return "";
  }
};

export const getAssetHistoryReason = (history) => {
  switch (history.reason) {
    case "sales":
    case "loss":
    case "remove":
      return "출고";
    case "newStock":
    case "returns":
    case "add":
      return "입고";
    case "transfer":
      if (history.quantity > 0) {
        return "입고";
      } else {
        return "출고";
      }
    case "force":
      if (history.totalCost > 0) {
        return "입고";
      } else if (history.totalCost < 0) {
        return "출고";
      } else {
        return "-";
      }
    default:
      return "";
  }
};

export const getOriginReason = (reason) => {
  switch (reason) {
    case "sales":
      return "판매";
    case "remove":
      return "임의차감";
    case "add":
      return "임의증가";
    case "loss":
      return "손실";
    case "newStock":
      return "입고";
    case "returns":
      return "반품입고";
    case "transfer":
      return "지점이동";
    case "force":
      return "임의조정";
    case "damaged":
      return "파손";
    case "request":
      return "요청";
    default:
      return "";
  }
};

export const getRequestStatusLabel = (_status) => {
  switch (_status) {
    case "created":
      return "요청대기";
    case "requested":
      return "요청완료";
    case "confirmed":
      return "출고준비";
    case "shipping":
      return "배송중";
    case "inspection":
      return "검수요청";
    case "complete":
      return "입고완료";

    default:
      return null;
  }
};

export const getRequestActionLabel = (_status, isAdmin = false) => {
  switch (_status) {
    case undefined:
      return "요청서 생성";
    case "created":
      return "요청서 업데이트";
    case "requested":
      return "요청승인";
    case "confirmed":
      return "출고처리";
    case "shipping":
      return "입고완료";
    case "inspection":
      return isAdmin ? "입고처리" : "검수요청";
    case "complete":
      return "";

    default:
      return "요청서 업데이트";
  }
};

export const getRequestTypeLabel = (_type) => {
  switch (_type) {
    case "new":
      return "입고요청서";
    case "returns":
      return "반품요청서";
    default:
      return "";
  }
};

export const isEditableRequest = (_status, isAdmin) => {
  if (isAdmin) {
    if (_status === "requested" || !_status) {
      return true;
    } else return false;
  } else {
    if (_status === "created" || !_status) {
      return true;
    } else return false;
  }
};

export const isDeletableRequest = (_status, isAdmin) => {
  if (isAdmin) {
    if (_status && ["created", "requested"].includes(_status)) {
      return true;
    } else return false;
  } else {
    if (_status && ["created"].includes(_status)) {
      return true;
    } else return false;
  }
};

export const isShippableRequest = (_status) => {
  if (_status === "confirmed") {
    return true;
  } else return false;
};

export const isAvailableInstructionDoc = (_status) => {
  if (["confirmed", "shipping", "inspection", "complete"].includes(_status)) {
    return true;
  } else return false;
};

export const toRequestedReturn = (
  request,
  { needConfirm, employeeId, fromLocationId, toLocationId },
) => {
  const now = new Date().toISOString();
  const requested = {
    ...request,
    status: "requested",
    fromLocationId: request.fromLocationId || fromLocationId,
    requestEmployeeId: employeeId,
    requestedAt: now,
  };
  if (needConfirm) return requested;
  return {
    ...requested,
    status: "confirmed",
    toLocationId: toLocationId || request.toLocationId || null,
    confirmEmployeeId: employeeId,
    confirmedAt: now,
    items: (request.items || []).map((i) => ({
      ...i,
      quantity: i.initQuantity,
    })),
  };
};

export const getReturnRequestConfirmMessage = (needConfirm) =>
  `${
    needConfirm
      ? "본사로 반품 요청이 전달되며, 본사 승인 후 반품 출고할 수 있습니다."
      : "반품준비 상태로 생성되어 바로 반품 출고할 수 있습니다."
  }\n반품 요청을 진행하시겠습니까?`;

export const REQUEST_APPLY_TRANSITIONS = [
  ["confirmed", "shipping"],
  ["shipping", "complete"],
  ["inspection", "complete"],
];

const initialState = {
  status: {
    isFetching: false,
    error: null,
  },
  histories: {
    byId: {},
    count: 0,
    currentItemCount: 0,
  },
  transferHistories: {
    byId: {},
    count: 0,
  },
  countHistories: {
    byId: {},
    count: 0,
  },
  summary: {
    bySkuId: {},
  },
  searched: {
    count: 0,
    list: [],
  },
  skuReport: [],
  searchedForExport: {
    list: [],
  },
  newRequests: {
    byId: {},
    count: 0,
  },
};

function status(state = initialState.status, action) {
  switch (action.type) {
    case actions.GET_INVENTORY_HISTORIES_BY_STORE:
    case actions.GET_INVENTORY_HISTORIES_BY_LOCATION:
    case actions.CREATE_INVENTORY_TRANSFER_HISTORY:
    case actions.UPDATE_INVENTORY_TRANSFER_HISTORY:
    case actions.DELETE_INVENTORY_TRANSFER_HISTORY:
    case actions.COMPLETE_INVENTORY_TRANSFER_HISTORY:
    case actions.GET_INVENTORY_TRANSFER_HISTORIES:
    case actions.GET_INVENTORY_COUNT_HISTORIES:
    case actions.GET_INVENTORY_HISTORY_SUMMARY:
    case actions.GET_SKU_INVENTORY_HISTORIES:
    case actions.UPDATE_INVENTORY_HISTORY:
    case actions.UPDATE_INVENTORY_HISTORY_NOTE:
    case actions.REFRESH_INVENTORY_HISTORY:
    case actions.GET_INVENTORY_HISTORIES_BY_GROUP_ID:
    case actions.GET_PRODUCT_INVENTORIES:
    case actions.GET_ALL_PRODUCT_INVENTORIES_FOR_EXPORT:
    case actions.GET_SKU_INVENTORY_REPORT:
    case actions.CREATE_NEW_REQUEST:
    case actions.UPDATE_NEW_REQUEST:
    case actions.CREATE_NEW_REQUESTS:
    case actions.UPDATE_NEW_REQUESTS:
    case actions.DELETE_NEW_REQUEST:
    case actions.GET_NEW_REQUESTS:
      return {
        isFetching: true,
        error: null,
      };
    case actions.SET_INVENTORY_STATUS_SUCCESS:
      return initialState.status;
    case actions.SET_INVENTORY_STATUS_FAILURE:
      return {
        isFetching: false,
        error: action.error,
      };

    default:
      return state;
  }
}

const summary = (state = initialState.summary, action) => {
  switch (action.type) {
    case actions.RECEIVE_INVENTORY_HISTORY_SUMMARY:
      return {
        ...state,
        bySkuId: action.list.reduce((obj, his) => {
          obj[his.skuId] = his;
          return obj;
        }, {}),
      };
    case actions.CLEAR_INVENTORY_HISTORY_SUMMARY:
      return initialState.summary;

    default:
      return state;
  }
};

const histories = (state = initialState.histories, action) => {
  let _histories = _.cloneDeep(state.byId);
  switch (action.type) {
    case actions.RECEIVE_INVENTORY_HISTORIES:
      return {
        ...state,
        byId: action.list.reduce(
          (obj, his) => {
            if (his.isLegacy) {
              obj[his.id + his.createdAt] = his;
            } else {
              obj[his.id] = his;
            }
            return obj;
          },
          action.override ? {} : _histories
        ),
        count: action.count,
      };
    case actions.RECEIVE_SKU_INVENTORY_HISTORIES:
      return {
        ...state,
        byId: action.list.reduce((obj, his) => {
          obj[his.id] = his;
          return obj;
        }, {}),
        currentItemCount: action.prevQuantity,
      };
    case actions.RECEIVE_INVENTORY_HISTORY:
      _histories[action.history.id] = {
        ...action.history,
        histories: [
          ...(_histories[action.history.id]?.histories || []),
          ...(action.history.histories || []),
        ],
      };
      return {
        ...state,
        byId: _histories,
        currentItemCount: action.count,
      };
    case actions.CLEAR_INVENTORY_HISTORIES:
      return initialState.histories;
    case actions.SIGN_OUT:
      return initialState.histories;
    default:
      return state;
  }
};

const transfer = (state = initialState.transferHistories, action) => {
  let _transfer = _.cloneDeep(state.byId);
  switch (action.type) {
    case actions.RECEIVE_INVENTORY_TRANSFER_HISTORIES:
      return {
        byId: action.list.reduce(
          (obj, his) => {
            obj[his.id] = his;
            return obj;
          },
          action.override ? {} : _transfer
        ),
        count: action.count,
      };
    case actions.RECEIVE_INVENTORY_TRANSFER_HISTORY:
      _transfer[action.history.id] = action.history;
      return {
        ...state,
        byId: _transfer,
      };
    case actions.RECEIVE_DELETED_TRANSFER_HISTORY:
      _transfer = Object.values(_transfer || {})
        .filter((t) => t.id !== action.history.id)
        .reduce((obj, _t) => {
          obj[_t.id] = _t;
          return obj;
        }, {});
      return {
        ...state,
        byId: _transfer,
      };
    default:
      return state;
  }
};

const count = (state = initialState.countHistories, action) => {
  let _count = _.cloneDeep(state.byId);
  switch (action.type) {
    case actions.RECEIVE_COUNT_HISTORIES:
      return {
        byId: action.list.reduce(
          (obj, his) => {
            obj[his.id] = his;
            return obj;
          },
          action.override ? {} : _count
        ),
        count: action.count,
      };
    default:
      return state;
  }
};

const searched = (state = initialState.searched, action) => {
  switch (action.type) {
    case actions.RECEIVE_PRODUCT_INVENTORIES:
      return {
        count: action.count,
        list: action.list,
      };
    case actions.CLEAR_PRODUCT_INVENTORIES:
      return initialState.searched;
    default:
      return state;
  }
};

const searchedForExport = (state = initialState.searchedForExport, action) => {
  switch (action.type) {
    case actions.RECEIVE_ALL_PRODUCT_INVENTORIES_FOR_EXPORT:
      return {
        list: action.list,
      };
    case actions.CLEAR_PRODUCT_INVENTORIES_FOR_EXPORT:
      return initialState.searchedForExport;
    default:
      return state;
  }
};

const skuReport = (state = initialState.skuReport, action) => {
  switch (action.type) {
    case actions.RECEIVE_SKU_INVENTORY_REPORT:
      return action.report;
    case actions.CLEAR_SKU_INVENTORY_REPORT:
      return initialState.skuReport;
    default:
      return state;
  }
};

export const getSkuQuantityByLocationId = (sku, locationId) => {
  let quantity = 0;
  for (let inv of sku.inventories || []) {
    if (inv.locationId === locationId) {
      quantity += inv.quantity;
    }
  }
  return quantity;
};

export const inventoryEditableLocations = (
  locations,
  organization,
  hasAccess
) => {
  return (locations || []).filter(
    (loc) => loc.id === organization.locationId || hasAccess
  );
};

const newRequests = (state = initialState.newRequests, action) => {
  let _requests = _.cloneDeep(state.byId);
  switch (action.type) {
    case actions.RECEIVE_NEW_REQUESTS:
      return {
        byId: action.list.reduce(
          (obj, his) => {
            obj[his.id] = his;
            return obj;
          },
          action.override ? {} : _requests
        ),
        count: action.count,
      };
    case actions.RECEIVE_NEW_REQUEST:
      _requests[action.history.id] = action.history;
      return {
        ...state,
        byId: _requests,
      };
    case actions.RECEIVE_DELETED_NEW_REQUEST:
      _requests = Object.values(_requests || {})
        .filter((t) => t.id !== action.history.id)
        .reduce((obj, _t) => {
          obj[_t.id] = _t;
          return obj;
        }, {});
      return {
        ...state,
        byId: _requests,
      };
    default:
      return state;
  }
};

export default combineReducers({
  status,
  histories,
  transfer,
  count,
  summary,
  searched,
  skuReport,
  searchedForExport,
  newRequests,
});
